import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import {
    Row,
    Col,
    Card,
    Button,
    Form,
    Alert,
    Spinner
} from 'react-bootstrap';
import { CircularProgress } from '@mui/material';
import { ArrowBack, PlayArrow } from '@mui/icons-material';
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "dashjs";
import "videojs-contrib-dash";
import { generatePPTAPI, getSignedUrl } from 'ServerAPI/serverAPI'; // same API
import CommonHeader from 'layouts/dashboard/CommonHeader';
import { useToast } from 'context/toast-context/ToastProvider';
import UploadFile from 'components/courses/add-new-course/steps/UploadFile';
import { useResponsive } from 'components/ResponsiveDesign/ResponsiveDesign';
import courseIcon from "../../assets/images/Courses.svg";

const PPTGeneration = () => {
    const [cookies] = useCookies();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { showToast } = useToast();
    const { isMobile, isTablet } = useResponsive();
    const videoRef = useRef(null);
    const playerRef = useRef(null);

    const { pptId } = useParams();
    const isViewingMode = !!pptId;

    const [formData, setFormData] = useState({
        title: '',
        text_content: ''
    });
    const [pptFileInfo, setPPtFileInfo] = useState(null);
    const [pdfFileInfo, setPdfFileInfo] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationError, setGenerationError] = useState(null);

    const [pptUrl, setPptUrl] = useState(null);
    const [pptLoading, setPptLoading] = useState(false);
    const [pptError, setPptError] = useState(null);
    const [pptData, setPptData] = useState(null);

    const [showInstructions, setShowInstructions] = useState(false);

    // Fetch PPT when in viewing mode
    useEffect(() => {
        if (isViewingMode && pptId) {
            fetchPptData();
        }
    }, [pptId, isViewingMode]);

    const fetchPptData = async () => {
        try {
            setPptLoading(true);
            setPptError(null);

            // const payload = {
            //     ppt: true,
            //     userId: cookies?.userId,
            //     pptId:pptId, 
            //     fileName: pptId,
            //     method: "GET",
            // };

            // const response = await getSignedUrl(payload);

            // if (response.data) {
            //     setPptUrl(response.data.preSignedUrl || response.data.objectUrl);
            //     setPptData(response.data);
            // } else {
            //     throw new Error('No PPT URL received');
            // }
        } catch (error) {
            console.error('Error fetching PPT:', error);
            setPptError(error.message || 'Failed to load PPT');
            showToast('Failed to load PPT', 'error');
        } finally {
            setPptLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setGenerationError(null);
    };

    const handlePPtFileChange = (file) => {
        setPPtFileInfo(file.target.files[0]);
        setGenerationError(null);
    };

    const handlePdfFileChange = (file) => {
        setPdfFileInfo(file.target.files[0]);
        setGenerationError(null);
    };

    const validateForm = () => {
        if (!formData.title.trim()) {
            setGenerationError('Title is required');
            return false;
        }
        if (!formData.text_content.trim() && !pdfFileInfo) {
            setGenerationError('Either a PDF file or prompt must be provided');
            return false;
        }
        return true;
    };

    const handleGeneratePpt = async () => {
        if (!validateForm()) return;

        try {
            setIsGenerating(true);
            setGenerationError(null);

            const payload = new FormData();
            payload.append('title', formData.title);
            payload.append('userId', cookies?.userId);

            if (formData.text_content.trim()) {
                payload.append('prompt', formData.text_content);
            }

            if (pdfFileInfo) {
                payload.append('pdf', pdfFileInfo);
            }

            if (pptFileInfo) {
                payload.append('ppt_template', pptFileInfo);
            }

            const response = await generatePPTAPI(payload);

            if (response.data) {
                showToast('PPT generation started successfully!', 'success');
                // setTimeout(() => {
                //     navigate('/content-generation-dashboard');
                // }, 1000);
            } else {
                showToast('PPT generation failed', "error");
            }
        } catch (error) {
            console.error('Error generating PPT:', error);
            setGenerationError(error.response?.data?.message || error.message || 'Failed to generate PPT');
            showToast('Failed to generate PPT', 'error');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleBack = () => {
        navigate('/content-generation-dashboard');
    };

    // Render generation form
    const renderGenerationForm = () => (
        <div>
            <div className="mb-4">
                <Form.Label className="fw-semibold">PPT Title *</Form.Label>
                <Form.Control
                    type="text"
                    placeholder="e.g., 'Quarterly Report Slides' or 'Marketing Strategy Deck'"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    disabled={isGenerating}
                    maxLength={100}
                />
                <Form.Text className="text-muted">
                    Enter a descriptive title for your PPT (max 100 characters)
                </Form.Text>
            </div>

            <div className="mb-4">
                <Form.Label className="fw-semibold">PPT Template </Form.Label>
                <div className="mb-3">
                    <Form.Label> Upload File</Form.Label>
                    <UploadFile
                        fileType="ppt"
                        handleFileChange={handlePPtFileChange}
                        showFileInfo={true}
                        selectedFile={pptFileInfo}
                        disabled={isGenerating}
                    />
                    <Form.Text className="text-muted">
                        Upload a PPT Template
                    </Form.Text>
                </div>
                <div className="mb-3">
                    <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => setShowInstructions(prev => !prev)}
                    >
                        {showInstructions ? 'Hide' : 'Show'} Instructions for Creating Template
                    </Button>
                </div>
                {showInstructions && (
                    <Alert variant="info" className="mt-3">
                        <div className="fw-semibold mb-2">How to create a PPT template:</div>
                        <ol className="mb-0 ps-3">
                            <li>Open PowerPoint and create a new blank presentation.</li>
                            <li>Go to <strong>View &gt; Slide Master</strong>.</li>
                            <li>In Slide Master view, delete all layouts except the first one (the large slide at the top).</li>
                            <li>Select the second layout (usually titled "Title and Content Layout"), then right-click and choose <strong>Delete Layout</strong>. Repeat until only the topmost master slide remains.</li>
                            <li>Customize the remaining master slide: set background, fonts, colors, logos, placeholders, etc.</li>
                            <li>Click <strong>Close Master View</strong>.</li>
                            <li>Delete all slides in the normal view except the first one (it will automatically use the single layout from the master).</li>
                            <li>Save the file as a <code>.potx</code> (PowerPoint Template) or <code>.pptx</code> file.</li>
                            <li>Upload the saved file here.</li>
                        </ol>
                    </Alert>
                )}
            </div>

            <div className="mb-4">
                <Form.Label className="fw-semibold">Content Source *</Form.Label>

                <div className="mb-3">
                    <Form.Label> Upload File</Form.Label>
                    <UploadFile
                        fileType="pdf"
                        handleFileChange={handlePdfFileChange}
                        showFileInfo={true}
                        selectedFile={pdfFileInfo}
                        disabled={isGenerating}
                    />
                    <Form.Text className="text-muted">
                        Upload a PDF or text file containing the content for your PPT
                    </Form.Text>
                </div>
            </div>

            {generationError && (
                <Alert variant="danger" className="mb-4">
                    {generationError}
                </Alert>
            )}

            <div className="d-flex gap-3">
                <Button
                    variant="secondary"
                    onClick={handleBack}
                    disabled={isGenerating}
                >
                    <ArrowBack fontSize="small" className="me-1" />
                    Back
                </Button>
                <Button
                    variant="primary"
                    onClick={handleGeneratePpt}
                    disabled={isGenerating || (!formData.text_content.trim() && !pdfFileInfo)}
                    className="flex-grow-1"
                >
                    {isGenerating ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Generating PPT...
                        </>
                    ) : (
                        <>
                            <PlayArrow fontSize="small" className="me-1" />
                            Generate PPT
                        </>
                    )}
                </Button>
            </div>

            {isGenerating && (
                <Alert variant="info" className="mt-4">
                    <div className="d-flex align-items-center">
                        <CircularProgress size={20} className="me-3" />
                        <div>
                            <div className="fw-semibold">PPT Generation in Progress</div>
                            <small className="text-muted">
                                Your PPT is being generated. This typically takes 5-10 minutes.
                                You can safely leave this page and check back later.
                            </small>
                        </div>
                    </div>
                </Alert>
            )}
        </div>
    );

    // Render viewer
    const renderPptViewer = () => (
        <div>
            {pptLoading && (
                <div className="text-center p-5">
                    <CircularProgress />
                    <div className="mt-2">Loading PPT...</div>
                </div>
            )}

            {pptError && (
                <Alert variant="danger" className="mb-4">
                    <Alert.Heading>Error Loading PPT</Alert.Heading>
                    <p className="mb-0">{pptError}</p>
                    <hr />
                    <div className="d-flex justify-content-end gap-2">
                        <Button variant="outline-danger" size="sm" onClick={fetchPptData}>
                            Retry
                        </Button>
                        <Button variant="secondary" size="sm" onClick={handleBack}>
                            Back to Dashboard
                        </Button>
                    </div>
                </Alert>
            )}

            {pptUrl && !pptLoading && !pptError && (
                <div>
                    <div className="mb-3">
                        <h5>PPT will download automatically</h5>
                    </div>

                    <iframe
                        src={pptUrl}
                        // style={{ width: '100%', height: '600px', border: 'none' }}
                        title="PPT Preview"
                    />

                    <div className="d-flex gap-3 mt-3">
                        <Button variant="secondary" onClick={handleBack}>
                            <ArrowBack fontSize="small" className="me-1" />
                            Back to Dashboard
                        </Button>
                        {pptData?.downloadUrl && (
                            <Button
                                variant="outline-primary"
                                href={pptData.downloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Download PPT
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="container-fluid">
            <Row>
                <Col>
                    <CommonHeader
                        text={isViewingMode ? "View PPT" : "Generate PPT"}
                        subText={isViewingMode ? "Preview your generated PPT" : "Create a new AI-generated PPT"}
                        icon={courseIcon}
                    />
                </Col>
            </Row>

            <Row className="mx-auto">
                <Col sm={isViewingMode ? 12 : 10} md={isViewingMode ? 10 : 8} className="mx-auto pt-5 px-0">
                    <Card>
                        <Card.Body className="p-4">
                            {isViewingMode ? renderPptViewer() : renderGenerationForm()}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default PPTGeneration;